import React from 'react';
import { cx } from '../../../shared/styles/runtimeCss';
import { FALLBACK_THEME, buildSectionShellStyles, pickResponsiveValue } from './rendererShared';

const modeAwareTimelineTop = (viewportWidth) => pickResponsiveValue(viewportWidth, '2rem', '2.5rem', '3rem');

function safeThemeValue(theme, path, fallback) {
  const value = path.split('.').reduce((acc, key) => acc?.[key], theme);
  return value ?? fallback;
}

function buildConnectorClass({ section, theme, css, viewportWidth, isMobile }) {
  if (!section.props.events || section.props.events.length <= 1) return null;

  const primaryColor = safeThemeValue(theme, 'colors.primary', FALLBACK_THEME.colors.primary);

  return css({
    position: 'absolute',
    left: isMobile ? '25px' : '50%',
    transform: isMobile ? 'none' : 'translateX(-50%)',
    width: isMobile ? '3px' : '4px',
    height: isMobile ? 'calc(100% - 4rem)' : 'calc(100% - 6rem)',
    background: `linear-gradient(to bottom, ${primaryColor}, ${primaryColor})`,
    top: isMobile ? '2rem' : modeAwareTimelineTop(viewportWidth),
    borderRadius: isMobile ? '3px' : '4px',
    boxShadow: isMobile ? undefined : `0 0 10px ${primaryColor}`,
    opacity: isMobile ? 0.8 : 1,
    zIndex: 1,
  });
}

function TimelineConnector(props) {
  const connectorClass = buildConnectorClass(props);
  if (!connectorClass) return null;
  return <div className={connectorClass} />;
}

function buildEventLayoutClasses({ theme, css, viewportWidth, isMobile, index, total }) {
  const isLeft = index % 2 === 0;
  const primaryColor = safeThemeValue(theme, 'colors.primary', FALLBACK_THEME.colors.primary);
  const backgroundColor = safeThemeValue(theme, 'colors.background', FALLBACK_THEME.colors.background);
  const secondaryColor = safeThemeValue(theme, 'colors.secondary', FALLBACK_THEME.colors.secondary);
  const dotAnchor = isMobile ? 'left' : isLeft ? 'right' : 'left';

  return {
    isLeft,
    containerClass: css({
      display: 'flex',
      justifyContent: isMobile ? 'flex-start' : 'center',
      alignItems: 'center',
      marginBottom: index === total - 1 ? '0' : isMobile ? '4rem' : '6rem',
      position: 'relative',
      zIndex: 2,
      paddingLeft: isMobile ? '3rem' : undefined,
    }),
    bodyClass: css({
      width: isMobile ? '100%' : '45%',
      backgroundColor,
      borderRadius: safeThemeValue(theme, 'borderRadius.large', FALLBACK_THEME.borderRadius.large),
      boxShadow: safeThemeValue(theme, 'shadows.medium', FALLBACK_THEME.shadows.medium),
      position: 'relative',
      marginLeft: isMobile ? '0' : isLeft ? '0' : 'auto',
      marginRight: isMobile ? '0' : isLeft ? 'auto' : '0',
      textAlign: isMobile ? 'left' : isLeft ? 'right' : 'left',
      border: `1px solid ${secondaryColor}`,
    }),
    bodyPaddingClass: css({
      padding: isMobile ? '1.5rem' : pickResponsiveValue(viewportWidth, '2rem', '2rem', '2.5rem'),
    }),
    dotClass: css({
      position: 'absolute',
      top: '50%',
      [dotAnchor]: isMobile ? '-65px' : '-70px',
      transform: 'translateY(-50%)',
      width: isMobile ? '30px' : '40px',
      height: isMobile ? '30px' : '40px',
      backgroundColor: primaryColor,
      borderRadius: '50%',
      border: `${isMobile ? 3 : 4}px solid ${backgroundColor}`,
      boxShadow: safeThemeValue(theme, 'shadows.medium', FALLBACK_THEME.shadows.medium),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: backgroundColor,
      fontSize: isMobile ? '0.9rem' : '1.2rem',
      fontWeight: 700,
      zIndex: 3,
    }),
    lineClass: css({
      position: 'absolute',
      top: '50%',
      [dotAnchor]: '-50px',
      transform: 'translateY(-50%)',
      width: isMobile ? '25px' : '50px',
      height: isMobile ? '2px' : '3px',
      background: primaryColor,
      boxShadow: isMobile ? undefined : `0 0 5px ${primaryColor}`,
      opacity: isMobile ? 0.8 : 1,
      zIndex: 2,
      display: 'block',
    }),
  };
}

function buildEventContentClasses({ theme, css, viewportWidth, isMobile }) {
  return {
    titleClass: css({
      color: safeThemeValue(theme, 'colors.primary', FALLBACK_THEME.colors.primary),
      marginBottom: '1rem',
      fontFamily: safeThemeValue(theme, 'fonts.heading', FALLBACK_THEME.fonts.heading),
      fontWeight: 600,
      fontSize: pickResponsiveValue(viewportWidth, '1.4rem', '1.6rem', '1.8rem'),
    }),
    dateClass: css({
      color: safeThemeValue(theme, 'colors.accent', FALLBACK_THEME.colors.accent),
      marginBottom: '1.5rem',
      fontFamily: safeThemeValue(theme, 'fonts.body', FALLBACK_THEME.fonts.body),
      fontWeight: 500,
      letterSpacing: '0.5px',
      fontSize: pickResponsiveValue(viewportWidth, '1.1rem', '1.2rem', '1.3rem'),
    }),
    descriptionClass: css({
      color: safeThemeValue(theme, 'colors.text', FALLBACK_THEME.colors.text),
      lineHeight: '1.8',
      marginBottom: '1.5rem',
      fontFamily: safeThemeValue(theme, 'fonts.body', FALLBACK_THEME.fonts.body),
      fontSize: pickResponsiveValue(viewportWidth, '1rem', '1.05rem', '1.1rem'),
    }),
    imageWrapClass: css({
      marginTop: '1.5rem',
      borderRadius: safeThemeValue(theme, 'borderRadius.medium', FALLBACK_THEME.borderRadius.medium),
      overflow: 'hidden',
      boxShadow: safeThemeValue(theme, 'shadows.small', FALLBACK_THEME.shadows.small),
      position: 'relative',
    }),
    imageClass: css({
      width: '100%',
      height: isMobile ? '200px' : pickResponsiveValue(viewportWidth, '200px', '220px', '250px'),
      objectFit: 'cover',
      transition: 'transform 0.3s ease',
    }),
  };
}

function TimelineEvent({ event, index, total, theme, css, viewportWidth, isMobile }) {
  const layoutClasses = buildEventLayoutClasses({
    theme,
    css,
    viewportWidth,
    isMobile,
    index,
    total,
  });
  const contentClasses = buildEventContentClasses({ theme, css, viewportWidth, isMobile });

  return (
    <div className={cx('timeline-event', layoutClasses.containerClass)}>
      <div className={cx('website-renderer__timeline-body', layoutClasses.bodyClass)}>
        <div className={layoutClasses.dotClass}>{index + 1}</div>
        <div className={layoutClasses.lineClass} />
        <div className={layoutClasses.bodyPaddingClass}>
          <h3 className={contentClasses.titleClass}>{event.title}</h3>
          <p className={contentClasses.dateClass}>{event.date}</p>
          <p className={contentClasses.descriptionClass}>{event.description}</p>
          {event.image ? (
            <div className={contentClasses.imageWrapClass}>
              <img src={event.image} alt={event.title} className={contentClasses.imageClass} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function TimelineSection({ section, theme, css, viewportWidth, isMobile, mode }) {
  const shellClass = css(buildSectionShellStyles(section, theme, mode, viewportWidth));
  const normalizedMobile = mode === 'viewer' ? isMobile : viewportWidth <= 768;
  const events = Array.isArray(section.props.events) ? section.props.events : [];

  return (
    <div className={cx('timeline-section', 'website-renderer__timeline-section', shellClass)}>
      <TimelineConnector
        section={section}
        theme={theme}
        css={css}
        viewportWidth={viewportWidth}
        isMobile={normalizedMobile}
      />
      {events.map((event, index) => (
        <TimelineEvent
          key={`${event.title || 'event'}-${index}`}
          event={event}
          index={index}
          total={events.length}
          theme={theme}
          css={css}
          viewportWidth={viewportWidth}
          isMobile={normalizedMobile}
        />
      ))}
    </div>
  );
}

export default TimelineSection;
